import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDate, fileUrl } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, MapPin, SignOut, SignIn, CaretLeft, CaretRight, Warning } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { toast } from "sonner";

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "super_admin", "hr"); // view-only — no punching
  const isManager = isAdmin || user.role === "department_manager" || user.is_department_manager;
  const [today, setToday] = useState(null);
  const [list, setList] = useState([]);
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState(isAdmin ? "team" : "today");
  const [corrections, setCorrections] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openCorrection, setOpenCorrection] = useState(false);
  const [corrForm, setCorrForm] = useState({ attendance_date: new Date().toISOString().slice(0, 10), correction_type: "full_day", reason: "", attachment_id: null });

  const load = () => {
    if (!isAdmin) api.get("/attendance/today").then((r) => setToday(r.data));
    api.get("/attendance").then((r) => setList(r.data));
    if (isAdmin) api.get("/attendance/dashboard").then((r) => setDash(r.data));
    api.get("/attendance/corrections").then((r) => setCorrections(r.data));
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submitCorrection = async () => {
    if (!corrForm.reason) return toast.error("Reason required");
    try { await api.post("/attendance/corrections", corrForm); toast.success("Correction request submitted"); setOpenCorrection(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const actCorrection = async (c, action) => {
    const comment = window.prompt(`Comment for ${action}`, "") || "";
    try { await api.post(`/attendance/corrections/${c.id}/action`, { action, comment }); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance" description={isAdmin ? "Monitor team attendance with live selfie + GPS verification." : "Mark your attendance with selfie & GPS."} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {!isAdmin && <TabsTrigger value="today" data-testid="att-tab-today">Today</TabsTrigger>}
          {!isAdmin && <TabsTrigger value="my" data-testid="att-tab-my">My History</TabsTrigger>}
          {!isAdmin && <TabsTrigger value="calendar" data-testid="att-tab-calendar">Monthly Calendar</TabsTrigger>}
          {isManager && <TabsTrigger value="team" data-testid="att-tab-team">Team</TabsTrigger>}
          {isAdmin && <TabsTrigger value="team-calendar" data-testid="att-tab-team-cal">Team Calendar</TabsTrigger>}
          <TabsTrigger value="corrections" data-testid="att-tab-corrections">Corrections</TabsTrigger>
          {isAdmin && <TabsTrigger value="dashboard" data-testid="att-tab-dash">Dashboard</TabsTrigger>}
        </TabsList>

        {!isAdmin && (
          <TabsContent value="today" className="mt-4">
            <CheckCard today={today} onSaved={load} />
          </TabsContent>
        )}

        {!isAdmin && (
          <TabsContent value="my" className="mt-4">
            <AttendanceTable items={list.filter((a) => a.employee_id === user.id)} isAdmin={false} />
          </TabsContent>
        )}

        {!isAdmin && (
          <TabsContent value="calendar" className="mt-4">
            <CalendarView employeeId={user.id} employeeName={user.full_name} />
          </TabsContent>
        )}

        {isManager && (
          <TabsContent value="team" className="mt-4">
            <AttendanceTable items={list.filter((a) => a.employee_id !== user.id)} isAdmin={isAdmin} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="team-calendar" className="mt-4">
            <TeamCalendarPicker employees={employees} />
          </TabsContent>
        )}

        <TabsContent value="corrections" className="mt-4 space-y-3">
          {!isAdmin && (
            <Dialog open={openCorrection} onOpenChange={setOpenCorrection}>
              <DialogTrigger asChild><Button className="btn-brand" data-testid="correction-new">Request Correction</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Attendance Correction Request</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Field label="Date"><Input type="date" value={corrForm.attendance_date} onChange={(e) => setCorrForm({ ...corrForm, attendance_date: e.target.value })} /></Field>
                  <Field label="Correction Type *">
                    <Select value={corrForm.correction_type} onValueChange={(v) => setCorrForm({ ...corrForm, correction_type: v })}>
                      <SelectTrigger data-testid="correction-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_day">Full Day</SelectItem>
                        <SelectItem value="half_day">Half Day</SelectItem>
                        <SelectItem value="leave">Leave</SelectItem>
                        <SelectItem value="week_off">Week Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Reason"><Textarea value={corrForm.reason} onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })} data-testid="correction-reason" /></Field>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpenCorrection(false)}>Cancel</Button><Button className="btn-brand" onClick={submitCorrection} data-testid="correction-submit">Submit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead>{isManager && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
              <TableBody>
                {corrections.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.employee_name}</TableCell>
                    <TableCell>{fmtDate(c.attendance_date)}</TableCell>
                    <TableCell className="capitalize text-xs">{(c.correction_type || "full_day").replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-sm opacity-80 max-w-md truncate">{c.reason}</TableCell>
                    <TableCell className="capitalize">{c.status}</TableCell>
                    {isManager && <TableCell className="text-right space-x-1">
                      {c.status === "pending" && <>
                        <Button size="sm" className="btn-brand" onClick={() => actCorrection(c, "approve")}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => actCorrection(c, "reject")}>Reject</Button>
                      </>}
                    </TableCell>}
                  </TableRow>
                ))}
                {corrections.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm opacity-60">No correction requests.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="dashboard" className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {dash && [
                { label: "Total", value: dash.total_employees },
                { label: "Present Today", value: dash.present_today, color: "text-green-600" },
                { label: "Absent Today", value: dash.absent_today, color: "text-red-600" },
                { label: "Late Today", value: dash.late_today, color: "text-amber-600" },
                { label: "Half Day", value: dash.half_day_today },
                { label: "WFH", value: dash.wfh_today },
              ].map((k) => (
                <Card key={k.label} className="p-4">
                  <div className="text-xs uppercase tracking-wider opacity-60">{k.label}</div>
                  <div className={`text-2xl font-semibold mt-1 ${k.color || ""}`} style={{ fontFamily: "Outfit, sans-serif" }}>{k.value}</div>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function CheckCard({ today, onSaved }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [mode, setMode] = useState(null); // "in" | "out"
  const [busy, setBusy] = useState(false);

  const startCam = async (which) => {
    setMode(which);
    setVideoReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error("Camera API not available in this browser");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
      // Defer DOM access — videoRef is set after re-render
      setTimeout(async () => {
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.onloadedmetadata = () => { setVideoReady(true); v.play().catch(() => undefined); };
        try { await v.play(); } catch { /* ignore */ }
      }, 50);
    } catch (e) {
      console.error("Camera error", e);
      toast.error(e?.message || "Cannot access camera. Please allow camera permission.");
      setStreaming(false);
      setMode(null);
    }
  };
  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreaming(false);
    setVideoReady(false);
    setMode(null);
  };

  const getPosition = () => new Promise((res, rej) => {
    if (!navigator.geolocation) return rej(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
  });

  const capture = async () => {
    if (!videoReady) return toast.error("Camera is still loading…");
    setBusy(true);
    try {
      const canvas = canvasRef.current; const video = videoRef.current;
      const w = video.videoWidth || 640, h = video.videoHeight || 480;
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(video, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("Could not capture image");
      // 1) request geolocation FIRST so prompt isn't blocked by upload spinner
      let lat = null, lng = null, address = "";
      try {
        const pos = await getPosition();
        lat = pos.coords.latitude; lng = pos.coords.longitude;
      } catch (geErr) {
        throw new Error(geErr?.message || "Location permission denied. Cannot record attendance without GPS.");
      }
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`, { headers: { "Accept-Language": "en" } });
        const j = await r.json(); address = j.display_name || "";
      } catch { /* reverse geocode optional */ }
      // 2) upload selfie
      const fd = new FormData();
      fd.append("file", new File([blob], `attendance-${mode}-${Date.now()}.jpg`, { type: "image/jpeg" }));
      fd.append("category", "attendance");
      const upRes = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const payload = { selfie_file_id: upRes.data.id, latitude: lat, longitude: lng, address, device_type: navigator.userAgent.slice(0, 80) };
      const endpoint = mode === "in" ? "/attendance/check-in" : "/attendance/check-out";
      await api.post(endpoint, payload);
      toast.success(mode === "in" ? "Checked in" : "Checked out");
      stopCam(); onSaved();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e?.message || "Failed to save attendance");
    }
    finally { setBusy(false); }
  };

  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;
  return (
    <Card className="p-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold mb-1">Today&apos;s Attendance</h3>
          <p className="text-sm opacity-70 mb-4">{checkedIn ? `Checked in at ${new Date(today.check_in).toLocaleTimeString()}` : "Not checked in yet."}</p>
          {checkedIn && <p className="text-sm opacity-70 mb-2">Status: <b className="capitalize">{today.status}</b></p>}
          {checkedIn && today.check_in_address && <p className="text-xs opacity-60 mb-2"><MapPin size={12} className="inline" /> {today.check_in_address}</p>}
          {checkedIn && today.check_in_maps && <a href={today.check_in_maps} target="_blank" rel="noreferrer" className="text-xs text-[#fd7e14] underline">Open in Google Maps</a>}
          {checkedOut && <div className="mt-3 text-sm">Checked out at {new Date(today.check_out).toLocaleTimeString()} • {today.hours_worked} hrs</div>}
          <div className="mt-4 flex gap-2">
            {!checkedIn && !streaming && <Button className="btn-brand" data-testid="att-checkin" onClick={() => startCam("in")}><SignIn size={16} className="mr-1.5" /> Check In</Button>}
            {checkedIn && !checkedOut && !streaming && <Button className="btn-brand" data-testid="att-checkout" onClick={() => startCam("out")}><SignOut size={16} className="mr-1.5" /> Check Out</Button>}
            {checkedOut && <span className="text-sm opacity-70">Done for the day ✓</span>}
          </div>
        </div>
        <div>
          {streaming ? (
            <div className="space-y-2">
              <video ref={videoRef} className="w-full rounded-md bg-black aspect-video" muted playsInline autoPlay />
              <div className="flex gap-2">
                <Button onClick={capture} disabled={busy || !videoReady} className="btn-brand" data-testid="att-capture"><Camera size={16} className="mr-1.5" /> {busy ? "Saving…" : videoReady ? "Capture & Save" : "Loading camera…"}</Button>
                <Button onClick={stopCam} variant="outline">Cancel</Button>
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
          ) : (
            <div className="aspect-video rounded-md border bg-grid flex items-center justify-center text-sm opacity-60" style={{ borderColor: "hsl(var(--border))" }}>
              Camera preview will appear here
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function AttendanceTable({ items, isAdmin }) {
  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Employee</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead>Location</TableHead>
          {isAdmin && <TableHead>Selfies</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {items.map((a) => (
            <TableRow key={a.id}>
              <TableCell>{fmtDate(a.date)}</TableCell>
              <TableCell>{a.employee_name}</TableCell>
              <TableCell className="text-sm">{a.check_in ? new Date(a.check_in).toLocaleTimeString() : "—"}</TableCell>
              <TableCell className="text-sm">{a.check_out ? new Date(a.check_out).toLocaleTimeString() : "—"}</TableCell>
              <TableCell>{a.hours_worked || "—"}</TableCell>
              <TableCell className="capitalize"><span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "present" ? "bg-green-100 text-green-700" : a.status === "late" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"} dark:bg-opacity-30`}>{a.status}</span></TableCell>
              <TableCell className="text-xs space-x-1">
                {a.check_in_maps && <a href={a.check_in_maps} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">In Map</a>}
                {a.check_out_maps && <a href={a.check_out_maps} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">Out Map</a>}
              </TableCell>
              {isAdmin && <TableCell className="text-xs space-x-1">
                {a.check_in_selfie && <a href={fileUrl(a.check_in_selfie)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">In</a>}
                {a.check_out_selfie && <a href={fileUrl(a.check_out_selfie)} target="_blank" rel="noreferrer" className="text-[#fd7e14] underline">Out</a>}
              </TableCell>}
            </TableRow>
          ))}
          {items.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-10 text-sm opacity-60">No records.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function TeamCalendarPicker({ employees }) {
  const [empId, setEmpId] = useState("");
  const sel = employees.find((e) => e.id === empId);
  return (
    <div className="space-y-3">
      <Card className="p-4">
        <Field label="Pick an employee">
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="max-w-md"><SelectValue placeholder="Select employee…" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </Card>
      {empId && <CalendarView employeeId={empId} employeeName={sel?.full_name || ""} />}
    </div>
  );
}

const STATUS_STYLES = {
  present: { bg: "bg-green-600 text-white", label: "Present" },
  late: { bg: "bg-amber-500 text-white", label: "Late" },
  half_day: { bg: "bg-yellow-300 text-yellow-900", label: "Half Day" },
  absent: { bg: "bg-red-600 text-white", label: "Absent" },
  week_off: { bg: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", label: "Week Off" },
  holiday: { bg: "bg-cyan-200 text-cyan-900", label: "Holiday" },
  leave: { bg: "bg-purple-300 text-purple-900", label: "On Leave" },
  punch_error: { bg: "bg-red-500 text-white", label: "Punch Error" },
  future: { bg: "bg-white border border-dashed border-slate-200 text-slate-400 dark:bg-slate-900 dark:text-slate-600", label: "—" },
};

function CalendarView({ employeeId, employeeName }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/attendance/calendar", { params: { year, month, employee_id: employeeId } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month, employeeId]);

  const goPrev = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long" });

  const firstWeekday = useMemo(() => new Date(year, month - 1, 1).getDay(), [year, month]); // 0=Sun
  const cells = useMemo(() => {
    if (!data?.days) return [];
    const arr = Array(firstWeekday).fill(null);
    return arr.concat(data.days);
  }, [data, firstWeekday]);

  return (
    <Card className="p-5" data-testid="att-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Insights — {employeeName || "You"}</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={goPrev} data-testid="cal-prev"><CaretLeft size={14} /></Button>
          <div className="text-base font-semibold w-44 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>{monthName} {year}</div>
          <Button size="sm" variant="outline" onClick={goNext} data-testid="cal-next"><CaretRight size={14} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 text-xs">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((w) => (
          <div key={w} className="text-center font-medium opacity-60 py-1.5">{w}</div>
        ))}
        {loading ? Array(35).fill(null).map((_, i) => <div key={i} className="aspect-square rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />) :
          cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const st = STATUS_STYLES[d.status] || STATUS_STYLES.future;
            return (
              <div key={i} className={`aspect-square rounded-lg flex items-center justify-center font-semibold relative ${st.bg}`} title={`${d.date} • ${st.label}`} data-testid={`cal-day-${d.day}`}>
                <span>{d.day}</span>
                {d.status === "punch_error" && <Warning size={10} className="absolute bottom-1 right-1" weight="fill" />}
              </div>
            );
          })}
      </div>

      {/* Legend */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {Object.entries({ leave: "On Leave", present: "Present", absent: "Absent", half_day: "Half Day", week_off: "Week Off", holiday: "Holiday", late: "Late", punch_error: "Punch Error" }).map(([k, label]) => (
          <div key={k} className="flex items-center gap-2">
            <span className={`inline-block w-4 h-4 rounded ${STATUS_STYLES[k].bg}`} />
            <span className="opacity-80">{label}</span>
          </div>
        ))}
      </div>

      {data?.summary && (
        <div className="mt-5 grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
          {Object.entries(data.summary).map(([k, v]) => (
            <div key={k} className="rounded-md border p-2" style={{ borderColor: "hsl(var(--border))" }}>
              <div className="font-semibold text-base" style={{ fontFamily: "Outfit, sans-serif" }}>{v}</div>
              <div className="opacity-60 capitalize">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
